/**
 * dropbox_search - Search for files in user's Dropbox
 * 
 * Fixes Bug #12: Use shared formatFileSize utility
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { dropboxService } from '../../services/dropboxService';
import { formatFileSize } from '../../utils/fileUtils';

export const dropbox_search: SimpleDocCommand = {
  id: 'dropbox_search',
  logicalName: 'dropbox_search',
  version: 'v1',
  
  description: `Search for files in the user's Dropbox.

**PREREQUISITES:**
User must have connected their Dropbox account in Settings → Integrations → Dropbox.

**SEARCH CAPABILITIES:**
- Search by filename
- Search by file content (Dropbox's full-text search)
- Filter by file type extensions

**EXAMPLES:**

Search for all PDFs with "meeting" in name or content:
{ "query": "meeting", "fileTypes": [".pdf"] }

Search for code files:
{ "query": "authentication", "fileTypes": [".js", ".ts", ".py"] }

General search:
{ "query": "Q4 report 2024" }

Search with custom result limit:
{ "query": "budget", "maxResults": 10 }

**LIMITS:** 
- Returns max 20 files per request by default
- Can request up to 100 files with maxResults parameter
- Use returned cursor for pagination

**FILE PATH FORMAT:**
All returned file paths start with "/" and can be used directly with dropbox_read.
`,

  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query (filename or content). Min 1 character, max 200.',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Query cannot be empty';
          }
          if (value.length > 200) {
            return 'Query too long (max 200 characters)';
          }
          return null;
        }
      },
      {
        name: 'fileTypes',
        type: 'array',
        description: 'Filter by file extensions (e.g., [".pdf", ".txt", ".md"]). Optional.',
        required: false,
        items: { type: 'string' },
        validator: (value: string[]) => {
          if (!value) return null;
          if (!Array.isArray(value)) {
            return 'fileTypes must be an array';
          }
          if (value.length > 10) {
            return 'Too many file types (max 10)';
          }
          // Ensure each starts with a dot
          for (const type of value) {
            if (!type.startsWith('.')) {
              return 'File types must start with a dot (e.g., ".pdf" not "pdf")';
            }
          }
          return null;
        }
      },
      {
        name: 'maxResults',
        type: 'number',
        description: 'Maximum number of results (default: 20, max: 100)',
        required: false,
        validator: (value: number) => {
          if (!value) return null;
          if (value < 1 || value > 100) {
            return 'maxResults must be between 1 and 100';
          }
          return null;
        }
      },
      {
        name: 'cursor',
        type: 'string',
        description: 'Pagination cursor from previous search (for getting more results)',
        required: false
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { query, fileTypes, maxResults = 20, cursor } = params;
    
    console.log('[dropbox_search] Searching Dropbox:', { query, fileTypes, maxResults, cursor });
    
    try {
      // Check if Dropbox is connected
      const status = await dropboxService.getStatus();
      
      if (!status.connected) {
        return {
          status: 'error',
          message: 'Dropbox not connected',
          error: {
            code: 'not_connected',
            category: 'validation',
            details: 'Please connect your Dropbox account in Settings → Integrations → Dropbox.'
          },
          format: 'plaintext'
        };
      }
      
      // Perform search
      const result = await dropboxService.searchFiles({
        query,
        fileTypes,
        maxResults,
        cursor
      });
      
      if (result.files.length === 0) {
        return {
          status: 'success',
          message: cursor 
            ? 'No more files found'
            : `No files found matching "${query}"`,
          data: {
            query,
            files: [],
            count: 0,
            hasMore: false
          },
          format: 'json'
        };
      }
      
      return {
        status: 'success',
        message: `Found ${result.files.length} file(s) in Dropbox${result.hasMore ? ' (more available)' : ''}`,
        data: {
          query,
          files: result.files.map(file => ({
            id: file.id,
            name: file.name,
            path: file.path,
            size: file.size,
            sizeFormatted: formatFileSize(file.size),
            modifiedAt: file.modifiedAt,
            mimeType: file.mimeType,
            isDownloadable: file.isDownloadable
          })),
          count: result.files.length,
          hasMore: result.hasMore,
          cursor: result.cursor,
          hint: result.hasMore 
            ? 'More results available. Use the returned cursor to get next page.'
            : 'Use dropbox_read with the file path to read the content.'
        },
        format: 'json'
      };
      
    } catch (error: any) {
      console.error('[dropbox_search] Error:', error);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        return {
          status: 'error',
          message: 'Dropbox not connected',
          error: {
            code: 'not_connected',
            category: 'validation',
            details: 'Please connect your Dropbox account in Settings → Integrations.'
          },
          format: 'plaintext'
        };
      }
      
      return {
        status: 'error',
        message: 'Failed to search Dropbox',
        error: error.response?.data?.detail || error.message,
        format: 'plaintext'
      };
    }
  }
};
